/**
 * ``AnnotationContextMenu`` — the right-click **"Copy measurements"** menu for
 * map annotations (#9; ADR-034). Because the on-map labels are rendered in WebGL
 * (a MapLibre symbol layer), their text can't be browser-selected — so this is
 * how an operator gets a shape's measurements out (the approach chosen with the
 * operator 2026-06-12, over an HTML-overlay re-render).
 *
 * A right-click (``contextmenu``) that lands on an annotation opens a small menu
 * at the cursor with **Copy measurements**, which writes the annotation's text
 * label + measurement readout (:func:`annotationCopyText`, in the operator's
 * units) to the clipboard. Right-clicking empty map does nothing (the browser
 * menu is left alone). The menu dismisses on any outside click, the map moving,
 * or Escape.
 *
 * The annotation under the cursor is picked exactly like the left-click
 * selection (``queryRenderedFeatures`` → ``annotation_id``; see
 * use-annotation-draw), so it stays in lock-step with what the operator sees.
 *
 * Cross-references:
 *   - ADR-034; @/annotations/annotation-label (annotationCopyText).
 *   - @/map/use-annotation-draw (the matching left-click selection pick).
 */
import { useCallback, useEffect, useState } from "react";
import type { Map as MapLibreMap, MapMouseEvent } from "maplibre-gl";
import { isMapUsable } from "@/map/map-ready";
import { useAnnotationsWithDrag } from "@/annotations/annotation-drag-store";
import { usePreferences } from "@/preferences/preferences-context";
import { annotationCopyText } from "@/annotations/annotation-label";

/** The open menu's viewport position + the annotation it targets. */
interface MenuState {
  readonly x: number;
  readonly y: number;
  readonly annotationId: string;
}

/** Render the right-click "Copy measurements" menu. Mount with the loaded map. */
export function AnnotationContextMenu({ map }: { map: MapLibreMap }): React.ReactElement | null {
  const annotations = useAnnotationsWithDrag();
  const units = usePreferences().workingState.units;
  const [menu, setMenu] = useState<MenuState | null>(null);

  // Right-click ON an annotation → open the menu at the cursor + suppress the
  // browser menu. Right-click elsewhere → close ours and leave the browser menu.
  useEffect(() => {
    if (!isMapUsable(map)) {
      return undefined;
    }
    const onContextMenu = (event: MapMouseEvent): void => {
      const hit = map
        .queryRenderedFeatures(event.point)
        .find((feature) => typeof feature.properties?.annotation_id === "string");
      const id = hit?.properties?.annotation_id as string | undefined;
      if (id === undefined) {
        setMenu(null);
        return;
      }
      event.preventDefault();
      setMenu({ x: event.originalEvent.clientX, y: event.originalEvent.clientY, annotationId: id });
    };
    map.on("contextmenu", onContextMenu);
    return () => {
      map.off("contextmenu", onContextMenu);
    };
  }, [map]);

  // Dismiss on any outside interaction: a click anywhere, the map starting to
  // move, or Escape.
  useEffect(() => {
    if (menu === null) {
      return undefined;
    }
    const close = (): void => setMenu(null);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close();
      }
    };
    map.on("movestart", close);
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      map.off("movestart", close);
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu, map]);

  const copyMeasurements = useCallback((): void => {
    if (menu === null) {
      return;
    }
    const annotation = (annotations ?? []).find((a) => a.annotation_id === menu.annotationId);
    if (annotation !== undefined) {
      const text = annotationCopyText(
        annotation,
        units?.distance ?? "nautical_miles",
        units?.coordinate_format ?? "decimal_degrees",
      );
      // Optional-chain: the Clipboard API is undefined in a non-secure context /
      // jsdom — never throw a readout-copy (operator-visibility, CLAUDE.md §8.1).
      void navigator.clipboard?.writeText(text);
    }
    setMenu(null);
  }, [menu, annotations, units]);

  if (menu === null) {
    return null;
  }

  return (
    <div
      role="menu"
      aria-label="Annotation actions"
      data-testid="annotation-context-menu"
      className="fixed z-50 min-w-[10rem] rounded border border-border bg-bg-elevated py-1 text-body-sm text-text-primary shadow-lg"
      style={{ left: menu.x, top: menu.y }}
    >
      <button
        type="button"
        role="menuitem"
        data-testid="annotation-copy-measurements"
        className="block w-full px-3 py-1.5 text-left hover:bg-bg-overlay focus:bg-bg-overlay focus:outline-none"
        // Stop the window 'click' dismiss from also firing for this click.
        onClick={(event) => {
          event.stopPropagation();
          copyMeasurements();
        }}
      >
        Copy measurements
      </button>
    </div>
  );
}
